import { apiFetch } from './client';

// R10 (#78, resolves D7) — read-only settlement integration status. The API
// exposes the endpoint HOST and configuration presence booleans only;
// credentials live server-side in Key Vault/env and never reach the SPA.

export interface SettlementStatus {
  mode: 'LIVE' | 'STUB';
  endpointHost: string | null;
  oaTokenConfigured: boolean;
  vaTokenConfigured: boolean;
  vaidConfigured: boolean;
  ospidsConfigured: boolean;
  pendingSyncCount: number;
  syncedCount: number;
  lastSuccessfulSync: string | null;
  deadLetterCount: number;
  lastDeadLetterAt: string | null;
  // Display-confirmation totals (#112): calls the CTIA platform confirmed a TSP
  // signed and passed to a handset, across the visible population.
  confirmationCount: number;
  lastConfirmedAt: string | null;
}

export interface CaseConfirmationRecord {
  callAt: string;
  tspName: string | null;
  claimsRequested: string | null;
  claimsPassed: string | null;
  dirid: string | null;
}

export interface CaseConfirmations {
  caseId: string;
  confirmationCount: number;
  lastConfirmedAt: string | null;
  recent: CaseConfirmationRecord[];
}

export interface SettlementProbeResult {
  ok: boolean;
  mode: 'LIVE' | 'STUB';
  detail: string;
}

export function getSettlementStatus(): Promise<SettlementStatus> {
  return apiFetch<SettlementStatus>('/settlement/status');
}

export function testSettlementConnection(): Promise<SettlementProbeResult> {
  return apiFetch<SettlementProbeResult>('/settlement/test-connection', { method: 'POST' });
}

export function getCaseConfirmations(caseId: string): Promise<CaseConfirmations> {
  return apiFetch<CaseConfirmations>(`/settlement/confirmations/${encodeURIComponent(caseId)}`);
}

// #108 — admin-only settlement OPERATIONS + CTIA §6 acceptance evidence.
// Dual-use: the CDR pull and enterprise delete are standing ops; the acceptance
// GET evidences them. All three are admin-gated server-side (403 otherwise).

export type CdrChain = 'test' | 'prod';

export interface CdrPullResult {
  chain: CdrChain;
  outcome: string;
  fetched: number;
  ingested: number;
  pruned: number;
  pages: number;
  cursorBefore: number;
  cursorAfter: number;
  adopted: boolean;
  legacyAdopted: boolean;
  nextEligibleAt: string | null;
}

export interface DeleteEnterpriseResult {
  eid: string;
  deleted: boolean;
  caseReset: string | null;
}

export interface CdrPullRun {
  id: string;
  chain: CdrChain;
  triggeredBy: string;
  actorId: string | null;
  startedAt: string;
  finishedAt: string | null;
  outcome: string;
  maxCount: number | null;
  pages: number | null;
  fetched: number | null;
  ingested: number | null;
  cursorBefore: number | null;
  cursorAfter: number | null;
  detail: string | null;
}

export interface AcceptanceReferenceCase {
  caseId: string;
  registrant: string;
  eid: string;
  dirid: string;
  deliveredTnCount: number;
  lastSyncAt: string | null;
}

export interface AcceptanceDelete {
  caseId: string;
  eid: string | null;
  source: string | null;
  actorId: string | null;
  at: string;
}

export interface AcceptanceCursor {
  id: string;
  lastTime: number;
  updatedAt: string;
}

export interface AcceptanceEvidence {
  mode: 'LIVE' | 'STUB';
  endpointHost: string | null;
  cdrTestMode: boolean;
  vaid: string | null;
  saidNote: string;
  referenceCases: AcceptanceReferenceCase[];
  recentDeletes: AcceptanceDelete[];
  cursors: AcceptanceCursor[];
  latestProdRun: CdrPullRun | null;
  /** Last 20 pull runs across BOTH chains, newest first. */
  recentRuns: CdrPullRun[];
  latestMultiPageRun: CdrPullRun | null;
}

export function getAcceptanceEvidence(): Promise<AcceptanceEvidence> {
  return apiFetch<AcceptanceEvidence>('/settlement/acceptance');
}

export function pullCdrsNow(chain: CdrChain, maxCount?: number): Promise<CdrPullResult> {
  return apiFetch<CdrPullResult>('/settlement/acceptance/cdr-pull', {
    method: 'POST',
    body: JSON.stringify(maxCount !== undefined ? { chain, maxCount } : { chain }),
  });
}

export function deleteEnterprise(eid: string, force: boolean): Promise<DeleteEnterpriseResult> {
  return apiFetch<DeleteEnterpriseResult>('/settlement/acceptance/delete-enterprise', {
    method: 'POST',
    body: JSON.stringify({ eid, force }),
  });
}

// CDR viewing (spec 2026-07-15) — filterable list + aggregate stats for the
// Settlement-page Confirmation Records section. tn is exact-match server-side.

export interface CdrFilters {
  from?: string;
  to?: string;
  tspid?: string;
  caseId?: string;
  eid?: string;
  tn?: string;
}

export interface CdrRecord {
  callAt: string;
  tspid: string;
  tspName: string | null;
  orig: string | null;
  dest: string | null;
  claimsRequested: string | null;
  claimsPassed: string | null;
  dirid: string | null;
  eid: string;
  caseId: string | null;
  ospid: string | null;
}

export interface CdrList {
  items: CdrRecord[];
  nextCursor: string | null;
}

export interface CdrStats {
  total: number;
  perTsp: { tspid: string; tspName: string | null; count: number }[];
  perDay: { day: string; count: number }[];
}

function cdrQueryString(filters: CdrFilters, extra: Record<string, string> = {}): string {
  const params = new URLSearchParams();
  for (const [k, v] of Object.entries({ ...filters, ...extra })) {
    if (v) params.set(k, v);
  }
  const qs = params.toString();
  return qs ? `?${qs}` : '';
}

export function fetchCdrs(filters: CdrFilters, opts: { before?: string; limit?: number } = {}): Promise<CdrList> {
  const extra: Record<string, string> = {};
  if (opts.before) extra.before = opts.before;
  if (opts.limit !== undefined) extra.limit = String(opts.limit);
  return apiFetch<CdrList>(`/settlement/cdrs${cdrQueryString(filters, extra)}`);
}

export function fetchCdrStats(filters: CdrFilters): Promise<CdrStats> {
  return apiFetch<CdrStats>(`/settlement/cdrs/stats${cdrQueryString(filters)}`);
}
